import { Theme } from "@mui/material/styles";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";
import { detectHmo } from "utils/hmoDetection";
import { HMO } from "constants/hmo";

const isLeumit = detectHmo() === HMO.LEUMIT;

interface Styles {
  [key: string]: any;
}

export const styles = (theme: Theme): Styles => ({
  headerContainer: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    // Natural height from content — parent flex layout allocates remaining space to body.
    height: "auto",
    position: "relative",
    background: (theme.palette.background as any).primary,
    padding: theme.spacing.spacing10,
  },
  headerContainer_SHORT: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    height: "auto",
    position: "relative",
    padding: theme.spacing.spacing10,
  },
  headerContainerBG: {},
  headerContainerLeumit: {
    alignItems: "stretch",
    background: theme.palette.white.main,
    padding: `0 0 ${theme.spacing.spacing2}`,
  },
  logoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: theme.width.full,
    padding: `${theme.spacing.spacing2} 0 ${theme.spacing.spacing8}`,
  },
  logoRowLeumit: {
    padding: `${theme.spacing.spacing12} ${theme.spacing.spacing10} ${theme.spacing.spacing8}`,
    boxShadow: theme.boxShadows.header,
  },
  brandLogo: {
    alignSelf: "flex-start",
    height: 36,
    width: theme.width.width62_5,
  },
  brandLogoLeumit: {
    height: 34,
    width: "auto",
    maxWidth: 120,
    objectFit: "contain",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.spacing4,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  userName: {
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",
  },
  languageRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    width: theme.width.full,
    padding: `${theme.spacing.spacing2} ${theme.spacing.spacing8} ${theme.spacing.spacing2}`,
    borderTop: `1px solid ${rgba(theme.palette.black.main, 0.06)}`,
  },
  stepContentLeumit: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: theme.width.full,
    padding: `0 ${theme.spacing.spacing10} 0`,
  },
  title: {
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: "pre-line",
    textAlign: "center",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.spacing2,
  },
  sectionTitle: {
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: (theme.palette.extraColors as { heading?: string })?.heading ?? (theme.palette.text as { main?: string }).main ?? theme.palette.text.primary,
    lineHeight: "20px",
  },
  sectionText: {
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightRegular,
    color: (theme.palette.text as { main?: string }).main ?? theme.palette.text.primary,
    lineHeight: "20px",
  },
  titleLeumit: {
    textAlign: "start",
    color: theme.palette.primary.main,
    marginTop: 0,
    fontSize: pxToRem(23),
    lineHeight: '24px',
  },
  titleWithMargin: {
    marginTop: theme.spacing.spacing14,
  },
  noPaddingTop: {
    paddingTop: 0,
  },
  descriptionWithMargin: {
    marginTop: theme.spacing.spacing10,
  },
  descriptionLeumit: {
    textAlign: "start",
  },
  largeTitle: {
    fontSize: theme.typography.size.size13,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: "pre-line",
    textAlign: "center",
  },
  descriptionSection: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.size.xs,
    width: theme.width.full,
  },
  descriptionBoxText: {
    alignSelf: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.spacing8,
  },
  descriptionBoxTextLeumit: {
    textAlign: "start",
    color: (theme.palette.text as any).main,
    gap: theme.spacing.spacing8,
    display: "flex",
    flexDirection: "column",
    paddingTop: theme.spacing.spacing6,
  },
  stepsBoxDefault: {
    marginBottom: theme.spacing.spacing10,
  },
  stepsBoxLeumit: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: theme.width.full,
    background: (theme.palette.background as any).soft,
    borderRadius: theme.borders.borderRadius.lg,
    padding: `${theme.spacing.spacing9} ${theme.spacing.spacing10}`,
    marginTop: theme.spacing.spacing4,
  },
  description: {
    whiteSpace: "pre-line",
  },
  loadingCircle: {
    width: theme.width.width8,
    height: 16,
    margin: `0 ${theme.spacing.spacing5}`,
    borderRadius: 100,
  },
  languageWrapper: {
    display: "flex",
    alignItems: "center",
  },
  hmoSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
    gap: theme.spacing.spacing5,
  },
  hmoLogo: {
    height: 50,
    width: 50,
    objectFit: "contain",
  },
  hmoName: {
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: "center",
    color: (theme.palette.text as any).main,
  },
  //remove after testing
  versionText: {
    position: "absolute",
    top: theme.spacing.spacing5,
    right: theme.spacing.spacing5,
    fontSize: theme.typography.size.xxs,
    fontWeight: theme.typography.fontWeightRegular,
    color: (theme.palette.text as any).main,
    zIndex: 1,
  },
  attentionTitle: {
    display: "flex",
    flexDirection: "column",
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.sm,
    lineHeight: "23px",
  },
  attentionDescription: {
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: "18px",
  },
  attentionContainer: {
    display: "flex",
    alignItems: "center",
    width: theme.width.full,
    minHeight: 82,
    padding: theme.spacing.spacing4,
    background: (theme.palette.background as any).softAlt,
    borderRadius: theme.borders.borderRadius.xl,
    marginTop: theme.spacing.spacing2,
  },
  iconBackground: {
    position: "relative",
    height: 58,
    width: 58,
    minHeight: 58,
    minWidth: 58,
    borderRadius: "100%",
    overflow: "hidden",
    margin: `0 ${theme.spacing.spacing4}`,
  },
  attentionIcon: {
    position: "absolute",
    top: `calc(52% - ${theme.spacing.spacing7})`,
    right: `calc(52% - ${theme.spacing.spacing7})`,
  },
  attentionIconImage: {
    '& path': {
      ...(isLeumit && {
        fill: theme.palette.extraColors?.alert,
      }),
    },
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    margin: `0 ${theme.spacing.spacing3}`,
  },

});
